import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Client-side admin auth. Passwords are never stored as plaintext —
 * only SHA-256 hashes are kept in the bundle, and the user-supplied
 * password is hashed in the browser before comparison.
 */

// SHA-256("Rawan05@")
const PASS_HASH = "c8afda79a96a924561bff7241bd599410ba16d9a9015458fddfac92be9bf1abf";

export const ADMIN_ACCOUNTS = [
  { email: "jamal@elhizazi.me", name: "Jamal", passHash: PASS_HASH },
  { email: "rawan@elhizazi.me", name: "Rawan", passHash: PASS_HASH },
];

const STORAGE_KEY = "rawan-auth-v2";

async function sha256Hex(text: string): Promise<string> {
  if (typeof crypto === "undefined" || !crypto.subtle) return "";
  const buf = new TextEncoder().encode(text);
  const out = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(out)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

type LoginResult = { ok: true } | { ok: false; error: string };

type AuthState = {
  isAuthed: boolean;
  ready: boolean;
  email: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { email?: string };
        if (parsed?.email) setEmail(parsed.email);
      }
    } catch {}
    setReady(true);
  }, []);

  const login: AuthState["login"] = async (e, p) => {
    const normalized = e.trim().toLowerCase();
    const account = ADMIN_ACCOUNTS.find((a) => a.email === normalized);
    if (!account) return { ok: false, error: "بيانات الدخول غير صحيحة" };
    const hash = await sha256Hex(p);
    if (hash !== account.passHash) return { ok: false, error: "بيانات الدخول غير صحيحة" };
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: account.email, ts: Date.now() }));
    setEmail(account.email);
    return { ok: true };
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setEmail(null);
  };

  return (
    <Ctx.Provider value={{ isAuthed: !!email, ready, email, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used inside AuthProvider");
  return c;
}
